// next-app/src/store/ui-store.ts
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { DTGSeason, DTGEvent } from '@/types';

// ============================================================================
// Types
// ============================================================================

export interface UIState {
  // Persisted state
  currentSeason: DTGSeason;

  // Non-persisted state
  appVersion: string;
  selectedEventID: number;
  isApprovedDealer: boolean;
  receivedCode: string;

  // Events can be stored here OR managed via TanStack Query
  // Using TanStack Query is recommended for server state
  events: DTGEvent[];

  // Legacy loading flags - consider using TanStack Query states instead
  isLoggingIn: boolean;
  isLoggingOut: boolean;
  isGettingAccount: boolean;
  isAPIProcessing: boolean;
  isResettingPassword: boolean;
  isGettingEvents: boolean;
  isSavingEvent: boolean;

  // Error states
  loginError: string;
  passwordError: string;
}

interface UIActions {
  setCurrentSeason: (season: DTGSeason) => void;
  setAppVersion: (version: string) => void;
  setSelectedEventID: (id: number) => void;
  setIsApprovedDealer: (approved: boolean) => void;
  setReceivedCode: (code: string) => void;
  setEvents: (events: DTGEvent[]) => void;

  // Loading flag setters
  setIsLoggingIn: (loading: boolean) => void;
  setIsLoggingOut: (loading: boolean) => void;
  setIsGettingAccount: (loading: boolean) => void;
  setIsAPIProcessing: (loading: boolean) => void;
  setIsResettingPassword: (loading: boolean) => void;
  setIsGettingEvents: (loading: boolean) => void;
  setIsSavingEvent: (loading: boolean) => void;

  // Error setters
  setLoginError: (error: string) => void;
  setPasswordError: (error: string) => void;

  // Reset
  resetUIState: () => void;
}

type UIStore = UIState & UIActions;

// ============================================================================
// Default Season
// ============================================================================

const defaultSeason: DTGSeason = {
  season_id: 6,
  season_name: 'Lincoln 100',
  start_date: new Date('2022-01-01'),
  end_date: new Date('2022-12-31'),
  enrollment_status: 'pre_enrollment',
};

// ============================================================================
// Initial State
// ============================================================================

const initialState: UIState = {
  currentSeason: defaultSeason,
  appVersion: process.env.NEXT_PUBLIC_APP_VERSION || '1.0.0',
  selectedEventID: 0,
  isApprovedDealer: false,
  receivedCode: '',
  events: [],
  isLoggingIn: false,
  isLoggingOut: false,
  isGettingAccount: false,
  isAPIProcessing: false,
  isResettingPassword: false,
  isGettingEvents: false,
  isSavingEvent: false,
  loginError: '',
  passwordError: '',
};

// ============================================================================
// Store Implementation
// ============================================================================

export const useUIStore = create<UIStore>()(
  persist(
    (set) => ({
      // State
      ...initialState,

      // Actions
      setCurrentSeason: (season: DTGSeason) => set({ currentSeason: season }),
      setAppVersion: (version: string) => set({ appVersion: version }),
      setSelectedEventID: (id: number) => set({ selectedEventID: id }),
      setIsApprovedDealer: (approved: boolean) => set({ isApprovedDealer: approved }),
      setReceivedCode: (code: string) => set({ receivedCode: code }),
      setEvents: (events: DTGEvent[]) => set({ events }),

      setIsLoggingIn: (loading: boolean) => set({ isLoggingIn: loading }),
      setIsLoggingOut: (loading: boolean) => set({ isLoggingOut: loading }),
      setIsGettingAccount: (loading: boolean) => set({ isGettingAccount: loading }),
      setIsAPIProcessing: (loading: boolean) => set({ isAPIProcessing: loading }),
      setIsResettingPassword: (loading: boolean) => set({ isResettingPassword: loading }),
      setIsGettingEvents: (loading: boolean) => set({ isGettingEvents: loading }),
      setIsSavingEvent: (loading: boolean) => set({ isSavingEvent: loading }),

      setLoginError: (error: string) => set({ loginError: error }),
      setPasswordError: (error: string) => set({ passwordError: error }),

      resetUIState: () =>
        set({
          ...initialState,
          // Preserve persisted currentSeason
        }),
    }),
    {
      name: 'ui-store',
      storage: createJSONStorage(() => localStorage),
      // Only persist currentSeason
      partialize: (state) => ({
        currentSeason: state.currentSeason,
      }),
    }
  )
);

// ============================================================================
// Selectors
// ============================================================================

export const selectCurrentSeason = (state: UIStore) => state.currentSeason;
export const selectEvents = (state: UIStore) => state.events;
export const selectIsLoading = (state: UIStore) =>
  state.isLoggingIn || state.isAPIProcessing || state.isGettingEvents;
